<template>
  <component :is="surveyType" :content="content" @submit="submit"></component>
</template>

<script lang="ts">
import { Score } from '@/store/modules/common/types';
import { Content } from '@forchange/storytime';
import { Vue, Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Choice from './Choice.vue';
import ShortAnswer from './ShortAnswer.vue';

const userModule = namespace('common');

export type Type = 'radio' | 'checkbox' | 'shortAnswer' | '单选' | '多选' | '简答';
export type Scoring = 'start' | 'end' | '开始' | '结束';
export type SurveyOptions = Array<SurveyOption>;
export type SurveyOption = {
  mark: string;
  content: string;
  score: OptionScore;
};
export type OptionScore = OptionScoreSingle | OptionScoreMultiple;
export type OptionScoreSingle = number;
export type OptionScoreMultiple = Array<{ type: string; value: number }>;

@Component
export default class Survey extends Vue {
  @Prop({ type: Object }) content!: Content & {
    type: Type;
    scoring: Scoring;
    scorebox: boolean;
    input: Array<string> | undefined;
    text: string | undefined;
    options: SurveyOptions;
    startTime?: number;
    totalScoreVisible?: string;
  };
  @userModule.State('score') score!: Score;

  pattern = {
    Choice,
    ShortAnswer
  };

  get SurveyTypeString() {
    switch (this.content.type) {
      case 'radio':
      case '单选':
        return 'radio';
      case 'checkbox':
      case '多选':
        return 'checkbox';
      default:
        return 'text';
    }
  }

  get surveyType() {
    let type;
    switch (this.SurveyTypeString) {
      case 'text':
        type = this.pattern.ShortAnswer;
        break;
      default:
        type = this.pattern.Choice;
        break;
    }

    return type;
  }

  get portalView() {
    const portalSequence = this.$store.state.common.portalSequence;
    return portalSequence[portalSequence.length - 1];
  }

  mounted() {
    this.initScore();
    this.initTrackData();
  }
  initTrackData() {
    if (!this.content?.startTime) {
      window.storyteller.updateAssignContent({
        $id: this.content.$id,
        $kind: this.content.$kind,
        ...{
          startTime: new Date().getTime()
        }
      });
    }
  }

  initScore() {
    if (this.portalView) return;
    if (this.content.input || this.content.text) return;
    if (['start', '开始'].includes(this.content.scoring) && this.content.options.some(option => !('score' in option))) return

    if (['start', '开始'].includes(this.content.scoring)) {
      const score = {
        status: 'start',
        type: 'single',
        visible: !!this.content.scorebox,
        value: 0,
        totalScoreVisible: ['true', '是'].includes(String(this.content.totalScoreVisible)),
        selection_id: this.content.$id,
        duration: 0,
        multipleValue: new Map()
      };
      const multiplescore = this.content?.options?.find(
        (item) => typeof item.score === 'object'
      )?.score;
      if (multiplescore) {
        const typeMap = new Map();
        (multiplescore as OptionScoreMultiple).forEach((item) => {
          typeMap.set(item.type, 0);
        });
        score.type = 'multiple';
        score.multipleValue = typeMap;
      }
      this.$store.commit('common/setScore', score);
      this.$emit('updateHistory');
    }
  }

  submit(data: { score: Map<string, number> | number; totalScore: number; duration: number }) {
    this.countScore(data.score);
    this.countTotalScore(data.totalScore);
    this.countDuration(data.duration);
    this.scoringEnd();
    window.next();
    setTimeout(() => {
      this.$emit('loaded');
    }, 300);
  }

  countDuration(duration: number) {
    this.$store.commit('common/setScore', {
      duration: this.score.duration + duration
    });
  }

  countScore(score: Map<string, number> | number) {
    typeof score === 'object' ? this.multipleCountScore(score) : this.singleCountScore(score);
  }

  countTotalScore(score: Map<string, number> | number) {
    if (typeof score === 'object') return;
    this.$store.commit('common/setScore', {
      totalValue: this.score.totalValue + score
    });
  }

  singleCountScore(score: number) {
    if (this.score.type === 'multiple' && this.score.status === 'start') {
      this.$Message.error({
        content: '多维计分模式下无法进行单一计分',
        time: 3000
      });
      return;
    }
    this.$store.commit('common/setScore', {
      value: this.score.value + score
    });
  }

  multipleCountScore(score: Map<string, number>) {
    if (this.score.type === 'single' && this.score.status === 'start') {
      this.$Message.error({
        content: '单分数模式下无法进行多维计分',
        time: 3000
      });
      return;
    }
    score.forEach((scoreValue, scoreKey) => {
      const storeScoreValue = this.score.multipleValue.get(scoreKey);
      this.score.multipleValue.set(scoreKey, scoreValue + (storeScoreValue || 0));
    });
  }

  scoringEnd() {
    if (!['end', '结束'].includes(this.content.scoring)) return;
    this.$store.commit('common/setScore', {
      status: '',
      visible: false
    });

    if (this.score.type === 'multiple') return;

    window.storyteller.createNextNode({
      $id: `${this.content.$id}-custom-grade`,
      $kind: 'Grade',
      ...{
        score: this.score.value,
        totalValue: this.score.totalValue,
        totalScoreVisible: this.score.totalScoreVisible
      }
    });
  }
}
</script>
